import React, { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, Save, ArrowUp, ArrowDown, Link2, Eye, EyeOff, X, Globe, ChevronDown, Languages, Loader2,
  Camera, Compass, Sparkles, MapPin, Calendar, BookOpen, Landmark, Phone, Utensils,
  Waves, Sun, Bed, Coffee, ShoppingBag, Music, Palette, Bike, Ship, Star, Heart, Building2, Trees, Tent, Car } from 'lucide-react';

// Icon library for tile picker (Feb 2026)
const TILE_ICONS = {
  Camera, Compass, Sparkles, MapPin, Calendar, BookOpen, Landmark, Phone, Utensils,
  Waves, Sun, Bed, Coffee, ShoppingBag, Music, Palette, Bike, Ship, Star, Heart,
  Building2, Trees, Tent, Car, Globe
};
export { TILE_ICONS };
import { adminApi } from '../../api';
import { PageHeader, Table, Modal, Field, Input, Select, I18nInputs, ImageUpload, SlugField } from './AdminUI';

const EMPTY = { slug: '', name: '', full_name: {it:'',en:''}, status: 'active', image: '', hero: '', logo: '', country: 'IT', latitude: null, longitude: null, timezone: 'Europe/Rome', external_municipality: '', external_events: '', services: {}, service_tiles: [], zone_id: '', show_in_home: false, order: 0 };

// Predefined internal routes for the tile linker
const INTERNAL_ROUTES = [
  { value: '/cosa-vedere', label: 'Cosa vedere (categorie)' },
  { value: '/experiences', label: 'YUP Experiences' },
  { value: '/storia', label: 'La storia della città' },
  { value: '/emergenze', label: 'Numeri di emergenza' },
];

export default function AdminCities() {
  const [items, setItems] = useState([]);
  const [zones, setZones] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => Promise.all([
    adminApi.cities().then(list => setItems(list.sort((a, b) => (a.order || 0) - (b.order || 0)))),
    adminApi.zones().then(list => setZones(list.sort((a, b) => (a.order || 0) - (b.order || 0)))),
  ]);
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...editing,
        order: Number(editing.order) || 0,
        show_in_home: !!editing.show_in_home,
        zone_id: editing.zone_id || null,
      };
      if (editing.id) await adminApi.updateCity(editing.id, payload);
      else await adminApi.createCity(payload);
      setEditing(null); load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const del = async (row) => {
    if (!window.confirm(`Eliminare la città "${row.slug}"?`)) return;
    await adminApi.deleteCity(row.id); load();
  };

  // Swap city order (move up/down)
  const move = async (row, dir) => {
    const idx = items.findIndex(c => c.id === row.id);
    const target = items[idx + dir];
    if (!target) return;
    const a = row.order || 0;
    const b = target.order || 0;
    // If both share the same order value, generate distinct positions
    const orderA = a === b ? idx + dir : b;
    const orderB = a === b ? idx : a;
    await Promise.all([
      adminApi.updateCity(row.id, { ...row, order: orderA }),
      adminApi.updateCity(target.id, { ...target, order: orderB })
    ]);
    load();
  };

  return (
    <div>
      <PageHeader title="Città" subtitle={`${items.length} città configurate — trascina o riordina con le frecce`} action={<button onClick={() => setEditing({...EMPTY, order: items.length})} className="btn-primary flex items-center gap-2" data-testid="admin-city-new-btn"><Plus size={16}/> Nuova</button>} />

      <CityDefaultsPanel />
      <ZonesPanel zones={zones} onChanged={load} />
      <QuickWidgetsPanel cities={items} onChanged={load} />
      <Table
        columns={[
          { key: 'order', label: 'Ordine', render: (r, i) => (
            <div className="flex flex-col items-center gap-0.5">
              <button
                onClick={() => move(r, -1)}
                disabled={i === 0}
                className="p-0.5 hover:bg-gray-100 rounded text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Sposta su"
                data-testid={`admin-city-move-up-${r.slug}`}
              ><ArrowUp size={14}/></button>
              <span className="text-xs text-gray-500 font-mono">{r.order ?? 0}</span>
              <button
                onClick={() => move(r, +1)}
                disabled={i === items.length - 1}
                className="p-0.5 hover:bg-gray-100 rounded text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Sposta giù"
                data-testid={`admin-city-move-down-${r.slug}`}
              ><ArrowDown size={14}/></button>
            </div>
          )},
          { key: 'image', label: '', render: r => r.image ? <img src={r.image.startsWith('http') ? r.image : `${process.env.REACT_APP_BACKEND_URL}${r.image}`} alt="" className="w-14 h-10 object-cover rounded"/> : '—' },
          { key: 'slug', label: 'Slug', render: r => <span className="font-mono text-xs">{r.slug}</span> },
          { key: 'name', label: 'Nome' },
          { key: 'full_name', label: 'Nome completo (IT)', render: r => r.full_name?.it || '—' },
          { key: 'zone_id', label: 'Zona / Home', render: r => {
            const z = zones.find(zz => zz.id === r.zone_id);
            return (
              <div className="flex flex-col gap-0.5">
                <span className="text-xs">{z ? z.name : <span className="text-gray-400">— nessuna</span>}</span>
                {z && r.show_in_home && (
                  <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded w-fit">★ Home</span>
                )}
              </div>
            );
          }},
          { key: 'status', label: 'Stato', render: r => <span className={`px-2 py-0.5 rounded text-xs font-semibold ${r.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>{r.status === 'active' ? 'Attiva' : 'Prossimamente'}</span> }
        ]}
        rows={items}
        actions={row => (
          <div className="flex gap-2 justify-end">
            <button onClick={() => setEditing({...row})} className="p-1.5 hover:bg-gray-100 rounded text-blue-600" data-testid={`admin-city-edit-${row.slug}`}><Edit2 size={15}/></button>
            <button onClick={() => del(row)} className="p-1.5 hover:bg-gray-100 rounded text-red-600" data-testid={`admin-city-delete-${row.slug}`}><Trash2 size={15}/></button>
          </div>
        )}
      />

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing?.id ? 'Modifica città' : 'Nuova città'} maxWidth="max-w-3xl">
        {editing && (
          <form onSubmit={save}>
            <div className="grid grid-cols-2 gap-3">
              <SlugField
                value={editing.slug}
                onChange={(v) => setEditing({ ...editing, slug: v })}
                isEditing={!!editing.id}
                entityLabel="città"
                placeholder="desenzano"
                testId="admin-city-slug"
              />
              <Field label="Nome breve"><Input required value={editing.name} onChange={e => setEditing({...editing, name: e.target.value})} placeholder="DESENZANO" /></Field>
            </div>
            <Field label="Nome completo per lingua"><I18nInputs value={editing.full_name} onChange={v => setEditing({...editing, full_name: v})} fallbackText={editing.name} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Stato"><Select value={editing.status} onChange={e => setEditing({...editing, status: e.target.value})}><option value="active">Attiva</option><option value="coming_soon">Prossimamente</option></Select></Field>
              <Field label="Ordine"><Input type="number" value={editing.order} onChange={e => setEditing({...editing, order: e.target.value})} /></Field>
            </div>

            {/* Home visibility: zone grouping + spotlight toggle (Feb 2026) */}
            <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <div className="text-xs font-semibold text-amber-900 uppercase tracking-wider mb-2">Visibilità in home</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Zona / Macro-area" hint="Se assegnata, la città compare dentro la card della zona invece che come card singola">
                  <Select
                    value={editing.zone_id || ''}
                    onChange={e => setEditing({...editing, zone_id: e.target.value || null})}
                    data-testid="admin-city-zone-select"
                  >
                    <option value="">— Nessuna (card singola in home)</option>
                    {zones.map(z => (
                      <option key={z.id} value={z.id}>{z.name}</option>
                    ))}
                  </Select>
                </Field>
                <Field label="Mostra anche come card singola in home" hint="Utile per dare risalto a una città anche se appartiene a una zona">
                  <label className="inline-flex items-center gap-2 mt-1.5 cursor-pointer" data-testid="admin-city-show-in-home-toggle">
                    <input
                      type="checkbox"
                      checked={!!editing.show_in_home}
                      disabled={!editing.zone_id}
                      onChange={e => setEditing({...editing, show_in_home: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <span className={`text-sm ${editing.zone_id ? 'text-gray-800' : 'text-gray-400'}`}>
                      {editing.zone_id ? 'Attivo' : '— disponibile solo con una zona assegnata'}
                    </span>
                  </label>
                </Field>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
              <Field label="Immagine card home" hint="Mostrata nella homepage nella lista città">
                <ImageUpload value={editing.image || ''} onChange={v => setEditing({...editing, image: v})} />
              </Field>
              <Field label="Immagine hero pagina città" hint="Sfondo grande della pagina della città">
                <ImageUpload value={editing.hero || ''} onChange={v => setEditing({...editing, hero: v})} />
              </Field>
            </div>

            <Field label="Link sito comune"><Input value={editing.external_municipality || ''} onChange={e => setEditing({...editing, external_municipality: e.target.value})} /></Field>
            <Field label="Link eventi (PDF/URL esterno)"><Input value={editing.external_events || ''} onChange={e => setEditing({...editing, external_events: e.target.value})} /></Field>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Field label="Paese (ISO 2)" hint="Es. IT, FR, DE — determina i numeri di emergenza auto-inseriti alla creazione">
                <Select value={editing.country || 'IT'} onChange={e => setEditing({...editing, country: e.target.value})}>
                  <option value="IT">🇮🇹 Italia</option>
                  <option value="FR">🇫🇷 Francia</option>
                  <option value="DE">🇩🇪 Germania</option>
                  <option value="ES">🇪🇸 Spagna</option>
                  <option value="CH">🇨🇭 Svizzera</option>
                  <option value="AT">🇦🇹 Austria</option>
                  <option value="GB">🇬🇧 Regno Unito</option>
                  <option value="US">🇺🇸 USA</option>
                  <option value="CA">🇨🇦 Canada</option>
                  <option value="AU">🇦🇺 Australia</option>
                  <option value="NZ">🇳🇿 Nuova Zelanda</option>
                  <option value="JP">🇯🇵 Giappone</option>
                  <option value="CN">🇨🇳 Cina</option>
                  <option value="AE">🇦🇪 Emirati Arabi Uniti</option>
                  <option value="NL">🇳🇱 Paesi Bassi</option>
                  <option value="BE">🇧🇪 Belgio</option>
                  <option value="PT">🇵🇹 Portogallo</option>
                  <option value="GR">🇬🇷 Grecia</option>
                  <option value="IE">🇮🇪 Irlanda</option>
                  <option value="DK">🇩🇰 Danimarca</option>
                  <option value="SE">🇸🇪 Svezia</option>
                  <option value="NO">🇳🇴 Norvegia</option>
                  <option value="FI">🇫🇮 Finlandia</option>
                  <option value="PL">🇵🇱 Polonia</option>
                  <option value="CZ">🇨🇿 Repubblica Ceca</option>
                  <option value="HR">🇭🇷 Croazia</option>
                  <option value="SI">🇸🇮 Slovenia</option>
                  <option value="HU">🇭🇺 Ungheria</option>
                  <option value="RO">🇷🇴 Romania</option>
                  <option value="BG">🇧🇬 Bulgaria</option>
                  <option value="TR">🇹🇷 Turchia</option>
                  <option value="MX">🇲🇽 Messico</option>
                  <option value="BR">🇧🇷 Brasile</option>
                  <option value="AR">🇦🇷 Argentina</option>
                  <option value="IN">🇮🇳 India</option>
                  <option value="ZA">🇿🇦 Sudafrica</option>
                </Select>
              </Field>
              <Field label="Latitudine" hint="Es. 45.4694 · usata dal widget meteo">
                <Input type="number" step="0.0001" value={editing.latitude ?? ''} onChange={e => setEditing({...editing, latitude: e.target.value === '' ? null : parseFloat(e.target.value)})} />
              </Field>
              <Field label="Longitudine" hint="Es. 10.5352">
                <Input type="number" step="0.0001" value={editing.longitude ?? ''} onChange={e => setEditing({...editing, longitude: e.target.value === '' ? null : parseFloat(e.target.value)})} />
              </Field>
              <Field label="Fuso orario" hint="Es. Europe/Rome">
                <Input value={editing.timezone || 'Europe/Rome'} onChange={e => setEditing({...editing, timezone: e.target.value})} />
              </Field>
            </div>

            <ServiceTilesEditor
              tiles={editing.service_tiles || []}
              citySlug={editing.slug}
              onChange={(list) => setEditing({...editing, service_tiles: list})}
            />

            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">Annulla</button>
              <button type="submit" className="btn-primary flex items-center gap-2" data-testid="admin-city-save-btn"><Save size={16}/> Salva</button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}

// ============ SERVICE TILES EDITOR ============
function ServiceTilesEditor({ tiles, citySlug, onChange }) {
  const list = Array.isArray(tiles) ? tiles : [];

  const update = (idx, patch) => {
    const next = list.map((t, i) => i === idx ? { ...t, ...patch } : t);
    onChange(next);
  };
  const remove = (idx) => {
    if (!window.confirm('Rimuovere questa tile?')) return;
    onChange(list.filter((_, i) => i !== idx));
  };
  const move = (idx, dir) => {
    const to = idx + dir;
    if (to < 0 || to >= list.length) return;
    const next = [...list];
    [next[idx], next[to]] = [next[to], next[idx]];
    onChange(next.map((t, i) => ({ ...t, order: i })));
  };
  const add = () => {
    const next = [...list, {
      id: 'tile_' + Math.random().toString(36).slice(2, 10),
      label: { it: 'Nuova tile', en: 'New tile' },
      image: '',
      link_type: 'internal',
      link: '',
      active: true,
      order: list.length,
    }];
    onChange(next);
  };
  const seedDefaults = () => {
    if (list.length && !window.confirm('Sovrascrivere le tile attuali con quelle di default?')) return;
    onChange([
      { id: 't_whatToSee', label: { it: 'Cosa Vedere', en: 'What to see', de: 'Sehenswürdigkeiten', fr: 'À voir', es: 'Qué ver' }, image: '', link_type: 'internal', link: '/cosa-vedere', active: true, order: 0 },
      { id: 't_experiences', label: { it: 'YUP Experiences', en: 'YUP Experiences' }, image: '', link_type: 'internal', link: '/experiences', active: true, order: 1 },
      { id: 't_events', label: { it: 'Eventi', en: 'Events', de: 'Events' }, image: '', link_type: 'external', link: '', active: true, order: 2 },
      { id: 't_story', label: { it: 'La Storia', en: 'The Story' }, image: '', link_type: 'internal', link: '/storia', active: true, order: 3 },
      { id: 't_municipality', label: { it: 'Il Comune', en: 'The Municipality' }, image: '', link_type: 'external', link: '', active: true, order: 4 },
      { id: 't_emergency', label: { it: 'Emergenze', en: 'Emergency' }, image: '', link_type: 'internal', link: '/emergenze', active: true, order: 5 },
    ]);
  };

  return (
    <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-4" data-testid="service-tiles-editor">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="font-semibold text-blue-900 text-sm">Tile personalizzate della pagina città</div>
          <p className="text-xs text-blue-800/80 mt-1">
            Le immagini caricate qui <strong>sovrascrivono</strong> il Template globale <em>solo</em> per questa città e questa tile. Lasciando l'immagine vuota, la pagina città eredita automaticamente lo sfondo dal Template. Aggiungi tile extra (es. EasyPark) solo dove servono davvero.
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          {list.length === 0 && (
            <button type="button" onClick={seedDefaults} data-testid="tiles-seed-btn"
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-white border border-blue-300 text-blue-700 hover:bg-blue-50 text-xs font-semibold">
              Popola con default
            </button>
          )}
          <button type="button" onClick={add} data-testid="tiles-add-btn"
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 text-xs font-semibold">
            <Plus size={13}/> Aggiungi tile
          </button>
        </div>
      </div>

      {list.length === 0 ? (
        <div className="text-center py-8 text-blue-700/60 text-sm border-2 border-dashed border-blue-200 rounded-lg bg-white/50">
          Nessuna tile personalizzata. Usa "Popola con default" per iniziare, oppure aggiungine una nuova.
          <br /><span className="text-xs text-blue-700/50">(Se lasci vuoto, verrà usato il sistema classico basato sulle "Immagini tile città" sopra)</span>
        </div>
      ) : (
        <div className="space-y-3">
          {list.map((tile, idx) => (
            <div key={tile.id || idx} data-testid={`tile-row-${idx}`}
                 className={`rounded-lg bg-white border p-3 ${tile.active === false ? 'opacity-60 border-slate-300' : 'border-blue-200'}`}>
              <div className="flex flex-col md:flex-row gap-3">
                {/* Image */}
                <div className="w-full md:w-40 flex-shrink-0 space-y-2">
                  <ImageUpload
                    value={tile.image || ''}
                    onChange={(v) => update(idx, { image: v })}
                    className="aspect-video"
                  />
                  {/* Icon fallback */}
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1 block">Icona (se manca foto)</label>
                    <div className="grid grid-cols-6 gap-1 max-h-24 overflow-y-auto p-1 rounded-md bg-slate-50 border border-slate-200">
                      <button type="button" onClick={() => update(idx, { icon: '' })}
                              data-testid={`tile-icon-none-${idx}`}
                              className={`aspect-square rounded flex items-center justify-center text-[9px] ${!tile.icon ? 'bg-blue-600 text-white' : 'bg-white text-slate-400 hover:bg-slate-100'}`}
                              title="Nessuna">–</button>
                      {Object.entries(TILE_ICONS).map(([name, Icn]) => (
                        <button key={name} type="button" onClick={() => update(idx, { icon: name })}
                                data-testid={`tile-icon-${name}-${idx}`}
                                title={name}
                                className={`aspect-square rounded flex items-center justify-center ${tile.icon === name ? 'bg-blue-600 text-white' : 'bg-white text-slate-500 hover:bg-blue-50 hover:text-blue-600'}`}>
                          <Icn size={14}/>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                {/* Fields */}
                <div className="flex-1 min-w-0 space-y-2">
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Titolo tile (per lingua)</div>
                    <I18nInputs
                      value={tile.label || {}}
                      onChange={(v) => update(idx, { label: v })}
                      fallbackText={tile.label?.it || ''}
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <Field label="Tipo di link">
                      <Select value={tile.link_type || 'internal'}
                              onChange={e => update(idx, { link_type: e.target.value, link: '' })}
                              data-testid={`tile-link-type-${idx}`}>
                        <option value="internal">Pagina interna</option>
                        <option value="external">URL esterno</option>
                      </Select>
                    </Field>
                    <Field label="Destinazione">
                      {tile.link_type === 'external' ? (
                        <Input placeholder="https://..." value={tile.link || ''}
                               onChange={e => update(idx, { link: e.target.value })}
                               data-testid={`tile-link-external-${idx}`} />
                      ) : (
                        <Select value={tile.link || ''}
                                onChange={e => update(idx, { link: e.target.value })}
                                data-testid={`tile-link-internal-${idx}`}>
                          <option value="">-- Scegli pagina --</option>
                          {INTERNAL_ROUTES.map(r => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                          ))}
                        </Select>
                      )}
                    </Field>
                    <Field label="Anteprima URL">
                      <Input readOnly value={
                        tile.link_type === 'external'
                          ? (tile.link || '(vuoto)')
                          : `/${citySlug || 'city'}${tile.link || ''}`
                      } className="bg-slate-50 text-slate-500 text-xs" />
                    </Field>
                  </div>
                </div>
              </div>

              {/* Row actions */}
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-blue-100 flex-wrap">
                <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0}
                        data-testid={`tile-up-${idx}`}
                        className="p-1.5 rounded-md bg-white border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-30">
                  <ArrowUp size={14}/>
                </button>
                <button type="button" onClick={() => move(idx, 1)} disabled={idx === list.length - 1}
                        data-testid={`tile-down-${idx}`}
                        className="p-1.5 rounded-md bg-white border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-30">
                  <ArrowDown size={14}/>
                </button>
                <button type="button" onClick={() => update(idx, { active: tile.active === false })}
                        data-testid={`tile-toggle-${idx}`}
                        className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold ${tile.active === false ? 'bg-slate-200 text-slate-600' : 'bg-emerald-100 text-emerald-700'}`}>
                  {tile.active === false ? <><EyeOff size={12}/> Nascosta</> : <><Eye size={12}/> Attiva</>}
                </button>
                <span className="text-xs text-slate-500 ml-auto">Posizione: {idx + 1}</span>
                <button type="button" onClick={() => remove(idx)}
                        data-testid={`tile-remove-${idx}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-red-50 border border-red-200 text-red-700 hover:bg-red-100 text-xs font-semibold">
                  <Trash2 size={12}/> Rimuovi
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ============ QUICK WIDGETS PANEL (partner tiles managed globally) ============
function QuickWidgetsPanel({ cities, onChanged }) {
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingKey, setEditingKey] = useState(null);
  const [selectedSlugs, setSelectedSlugs] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi.listQuickWidgets();
      setWidgets(r.widgets || []);
    } catch (_) { setWidgets([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startEdit = (w) => {
    setEditingKey(w.key);
    setSelectedSlugs((w.cities_with_widget || []).map(c => c.slug));
  };

  const toggle = (slug) => {
    setSelectedSlugs(prev => prev.includes(slug) ? prev.filter(s => s !== slug) : [...prev, slug]);
  };

  const apply = async () => {
    setSaving(true);
    try {
      const r = await adminApi.applyQuickWidget(editingKey, selectedSlugs);
      alert(`✅ Widget aggiornato:\n\n+ ${r.added} aggiunti\n- ${r.removed} rimossi\n= ${r.unchanged} invariati`);
      setEditingKey(null);
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      alert('Errore: ' + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  if (loading) return null;

  return (
    <div className="mb-6 rounded-xl border border-purple-200 bg-gradient-to-br from-purple-50 to-fuchsia-50 p-4" data-testid="quick-widgets-panel">
      <div className="flex items-center gap-2 mb-2">
        <Music size={18} className="text-purple-700"/>
        <h3 className="font-semibold text-purple-900 text-sm">Widget partner — mostra su più città in un click</h3>
      </div>
      <p className="text-xs text-purple-800/80 mb-3">
        Un widget partner è una tile promozionale (es. Radio Studio Più) che compare nella pagina città. Qui selezioni in quali città farla apparire con un solo click — YUP la aggiunge/rimuove automaticamente in tutte quelle che hai scelto.
      </p>

      <div className="grid gap-3">
        {widgets.map(w => {
          const cfg = w.config;
          const activeCount = w.cities_with_widget.length;
          const isEditing = editingKey === w.key;
          return (
            <div key={w.key} className="rounded-lg bg-white border border-purple-200 p-3" data-testid={`quick-widget-${w.key}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <img src={cfg.image} alt={cfg.label?.it}
                     className="w-14 h-14 rounded-lg object-cover border border-purple-200 flex-shrink-0"/>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-slate-800">{cfg.label?.it}</div>
                  <a href={cfg.link} target="_blank" rel="noopener noreferrer"
                     className="text-xs text-purple-700 hover:underline truncate block">
                    {cfg.link}
                  </a>
                  <div className="text-[11px] text-slate-500 mt-1">
                    Attualmente visibile su: <strong className="text-emerald-700">{activeCount}</strong> {activeCount === 1 ? 'città' : 'città'}
                    {activeCount > 0 && (
                      <span className="text-slate-600"> ({w.cities_with_widget.map(c => c.name).join(', ')})</span>
                    )}
                  </div>
                </div>
                {!isEditing && (
                  <button type="button" onClick={() => startEdit(w)}
                          data-testid={`configure-widget-${w.key}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-purple-600 text-white text-xs font-semibold hover:bg-purple-700 flex-shrink-0">
                    Configura
                  </button>
                )}
              </div>

              {isEditing && (
                <div className="mt-3 pt-3 border-t border-purple-100">
                  <div className="text-xs font-semibold text-slate-700 mb-2">Seleziona le città in cui mostrare il widget:</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
                    {cities.map(c => {
                      const checked = selectedSlugs.includes(c.slug);
                      return (
                        <label key={c.slug}
                               data-testid={`widget-city-${c.slug}`}
                               className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer text-sm ${checked ? 'bg-purple-600 text-white border-purple-600' : 'bg-white border-slate-200 text-slate-700 hover:bg-purple-50'}`}>
                          <input type="checkbox" checked={checked} onChange={() => toggle(c.slug)}
                                 className="accent-purple-600"/>
                          <span className="truncate">{c.name}</span>
                        </label>
                      );
                    })}
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button type="button" onClick={() => setEditingKey(null)}
                            className="px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 text-xs hover:bg-slate-50">
                      Annulla
                    </button>
                    <button type="button" onClick={apply} disabled={saving}
                            data-testid="apply-widget-btn"
                            className="inline-flex items-center gap-1 px-4 py-1.5 rounded-md bg-purple-600 text-white text-xs font-semibold hover:bg-purple-700 disabled:opacity-50">
                      {saving ? 'Applicazione...' : `Applica a ${selectedSlugs.length} città`}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}



/**
 * CityDefaultsPanel — centralised default images for the standard sections that
 * appear on EVERY city page. Uploaded once here, then re-used automatically
 * every time a new city is created (backend fills `service_tiles[].image` from
 * these settings). `Cosa vedere` and `Il comune` are intentionally NOT here:
 * they must show the real photo of that specific city.
 */
function CityDefaultsPanel() {
  const KEYS = [
    { key: 'city_default_whatToSee_image',   label: 'Cosa vedere',    hint: 'Immagine di sfondo del bottone "Cosa vedere" (non della pagina interna)' },
    { key: 'city_default_events_image',      label: 'Eventi',         hint: 'Immagine di default per la tile "Eventi"' },
    { key: 'city_default_story_image',       label: 'La storia',      hint: 'Immagine di default per la tile "La storia"' },
    { key: 'city_default_emergency_image',   label: 'Emergenze',      hint: 'Immagine di default per la tile "Emergenze"' },
    { key: 'city_default_experiences_image', label: 'YUP Experiences', hint: 'Immagine di default per la tile "YUP Experiences"' },
    { key: 'city_default_easypark_image',    label: 'EasyPark',       hint: 'Logo/immagine della tile "EasyPark" — link fisso https://www.easypark.com/it-it, ogni nuova città lo eredita, ogni città esistente può disattivarla singolarmente' },
  ];
  const [values, setValues] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    adminApi.getSettings().then(all => {
      const v = {};
      for (const k of KEYS) v[k.key] = all[k.key] || '';
      setValues(v);
    }).catch(() => {});
  }, []);

  const saveOne = async (key, val) => {
    setSavingKey(key);
    try {
      await adminApi.setSetting(key, val);
      setValues(prev => ({ ...prev, [key]: val }));
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="mb-4 rounded-lg border border-indigo-200 bg-indigo-50/60" data-testid="city-defaults-panel">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        data-testid="city-defaults-toggle"
      >
        <div>
          <div className="font-semibold text-indigo-900 text-sm">Template città — immagini di default per sezioni fisse</div>
          <p className="text-xs text-indigo-800/80 mt-0.5">
            Caricali una volta sola: le nuove città li erediteranno in automatico per Cosa vedere, Eventi, La storia, Emergenze e YUP Experiences. Solo "Il comune" resta da compilare per ogni città (foto del municipio reale).
          </p>
        </div>
        <ChevronDown size={18} className={`text-indigo-700 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {KEYS.map(k => (
            <div key={k.key} className="bg-white rounded-md border border-indigo-200 p-3" data-testid={`city-default-${k.key}`}>
              <div className="text-xs font-semibold text-slate-700 mb-1">{k.label}</div>
              <ImageUpload
                value={values[k.key] || ''}
                onChange={(v) => saveOne(k.key, v || '')}
              />
              {savingKey === k.key && <div className="text-[10px] text-indigo-600 mt-1">Salvataggio...</div>}
              <p className="text-[10px] text-slate-500 mt-1">{k.hint}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ============ ZONES PANEL (macro-areas that group cities on the home) ============
// A "Zone" is a macro-area (e.g. "Lago di Garda") that renders on the homepage
// as a single card, replacing the individual cards of every city assigned to
// it. Zones and standalone cities share the same `order` field so the admin
// can freely interleave them from the Città table above.
const EMPTY_ZONE = { slug: '', name: '', full_name: { it: '', en: '' }, image: '', active: true, order: 0 };

function ZonesPanel({ zones, onChanged }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [retranslating, setRetranslating] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...editing, order: Number(editing.order) || 0, active: !!editing.active };
      if (editing.id) await adminApi.updateZone(editing.id, payload);
      else await adminApi.createZone(payload);
      setEditing(null);
      if (onChanged) await onChanged();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const retranslate = async () => {
    if (!editing?.id) return;
    if (!window.confirm('Rigenerare le traduzioni per EN, DE, FR, ES, ZH, AR?\n\nEventuali modifiche manuali sui nomi tradotti verranno sovrascritte.')) return;
    setRetranslating(true);
    try {
      const r = await adminApi.retranslateZone(editing.id);
      setEditing(prev => ({ ...prev, full_name: r.full_name || prev.full_name }));
      if (onChanged) await onChanged();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore durante la traduzione');
    } finally {
      setRetranslating(false);
    }
  };

  const del = async (z) => {
    if (!window.confirm(`Eliminare la zona "${z.name}"?\n\nLe città assegnate torneranno a comparire come card singole in home.`)) return;
    await adminApi.deleteZone(z.id);
    if (onChanged) await onChanged();
  };

  const move = async (z, dir) => {
    const idx = zones.findIndex(zz => zz.id === z.id);
    const target = zones[idx + dir];
    if (!target) return;
    const a = z.order || 0;
    const b = target.order || 0;
    const orderA = a === b ? idx + dir : b;
    const orderB = a === b ? idx : a;
    await Promise.all([
      adminApi.updateZone(z.id, { ...z, order: orderA }),
      adminApi.updateZone(target.id, { ...target, order: orderB }),
    ]);
    if (onChanged) await onChanged();
  };

  return (
    <div className="mb-4 rounded-lg border border-teal-200 bg-teal-50/60" data-testid="zones-panel">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        data-testid="zones-panel-toggle"
      >
        <div>
          <div className="font-semibold text-teal-900 text-sm">Zone / Macro-aree ({zones.length})</div>
          <p className="text-xs text-teal-800/80 mt-0.5">
            Raggruppa più città sotto un'unica card in home (es. "Lago di Garda"). Assegna la zona a ciascuna città dal form di modifica città. Le città senza zona restano come card singole (comportamento attuale).
          </p>
        </div>
        <ChevronDown size={18} className={`text-teal-700 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setEditing({ ...EMPTY_ZONE, order: zones.length })}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-teal-600 text-white text-xs font-semibold hover:bg-teal-700"
              data-testid="zone-new-btn"
            >
              <Plus size={13}/> Nuova zona
            </button>
          </div>

          {zones.length === 0 ? (
            <div className="text-center py-6 text-teal-800/70 text-sm border-2 border-dashed border-teal-200 rounded-lg bg-white/60">
              Nessuna zona ancora creata. Crea "Lago di Garda", "Costa Amalfitana", ecc. e poi assegna le città dal loro form.
            </div>
          ) : (
            <div className="space-y-2">
              {zones.map((z, idx) => (
                <div key={z.id} data-testid={`zone-row-${z.slug}`} className="bg-white rounded-md border border-teal-200 p-3 flex flex-col md:flex-row md:items-center gap-3">
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => move(z, -1)} disabled={idx === 0}
                            className="p-1 text-teal-700 disabled:opacity-30" aria-label="Su">
                      <ArrowUp size={14}/>
                    </button>
                    <span className="font-mono text-xs text-slate-500 w-6 text-center">{z.order ?? 0}</span>
                    <button type="button" onClick={() => move(z, 1)} disabled={idx === zones.length - 1}
                            className="p-1 text-teal-700 disabled:opacity-30" aria-label="Giù">
                      <ArrowDown size={14}/>
                    </button>
                  </div>
                  {z.image ? (
                    <img src={z.image.startsWith('http') ? z.image : `${process.env.REACT_APP_BACKEND_URL}${z.image}`}
                         alt="" className="w-16 h-10 object-cover rounded"/>
                  ) : (
                    <div className="w-16 h-10 bg-slate-100 rounded flex items-center justify-center text-slate-400"><MapPin size={14}/></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-800 text-sm">{z.name}</div>
                    <div className="text-xs text-slate-500 font-mono">/zone/{z.slug} · {z.city_count || 0} {(z.city_count || 0) === 1 ? 'città' : 'città'}{z.active ? '' : ' · disattiva'}</div>
                  </div>
                  <div className="flex gap-1">
                    <button type="button" onClick={() => setEditing({ ...z })} className="p-1.5 rounded hover:bg-teal-50 text-teal-700" data-testid={`zone-edit-${z.slug}`}><Edit2 size={14}/></button>
                    <button type="button" onClick={() => del(z)} className="p-1.5 rounded hover:bg-red-50 text-red-600" data-testid={`zone-delete-${z.slug}`}><Trash2 size={14}/></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing?.id ? 'Modifica zona' : 'Nuova zona'} maxWidth="max-w-2xl">
        {editing && (
          <form onSubmit={save}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <SlugField
                value={editing.slug}
                onChange={(v) => setEditing({ ...editing, slug: v })}
                isEditing={!!editing.id}
                entityLabel="zona"
                placeholder="lago-di-garda"
                testId="zone-form-slug"
              />
              <Field label="Nome (mostrato in card)"><Input required value={editing.name} onChange={e => setEditing({...editing, name: e.target.value})} placeholder="Lago di Garda" data-testid="zone-form-name" /></Field>
            </div>
            <Field
              label={
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span>Nome per lingua (auto-tradotto)</span>
                  {editing.id && (
                    <button
                      type="button"
                      onClick={retranslate}
                      disabled={retranslating}
                      data-testid="zone-retranslate-btn"
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-white border border-teal-300 text-teal-700 hover:bg-teal-50 text-[11px] font-semibold disabled:opacity-50"
                    >
                      {retranslating ? <Loader2 size={11} className="animate-spin"/> : <Languages size={11}/>}
                      {retranslating ? 'Traduzione…' : 'Ritraduci ora'}
                    </button>
                  )}
                </div>
              }
              hint="Le lingue vuote vengono tradotte in automatico dal nome italiano al salvataggio. Modifica manualmente per sovrascrivere la traduzione."
            >
              <I18nInputs value={editing.full_name} onChange={v => setEditing({...editing, full_name: v})} fallbackText={editing.name} />
            </Field>
            <Field label="Immagine card home" hint="Foto grande mostrata sulla card della zona nella home">
              <ImageUpload value={editing.image || ''} onChange={v => setEditing({...editing, image: v})} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Ordine in home"><Input type="number" value={editing.order} onChange={e => setEditing({...editing, order: e.target.value})} /></Field>
              <Field label="Stato">
                <Select value={editing.active ? 'active' : 'inactive'} onChange={e => setEditing({...editing, active: e.target.value === 'active'})}>
                  <option value="active">Attiva (visibile in home)</option>
                  <option value="inactive">Disattiva (nascosta)</option>
                </Select>
              </Field>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">Annulla</button>
              <button type="submit" className="btn-primary flex items-center gap-2" data-testid="zone-save-btn"><Save size={16}/> Salva</button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
